/**
 * Customer Form - Shared logic for modal and full-page forms.
 *
 * Handles: live error-clearing, customer type toggle, VAT live duplicate
 * check, location formset (PR 2.1f-a: site contact data lives flat on
 * each Location row — there's no separate contact formset anymore).
 *
 * PR-4b (contract refactor, 2026-05-25) removed the customer-level
 * billing-mode toggle (the « Invoice Settings » card was dropped from
 * the form). The equivalent now lives on the WorkSite form and is
 * wired by ``worksite-form.js`` via an HTMX swap.
 *
 * Validation strategy: 100% server-side on submit.
 * `apps/office/forms_create.py::CustomerForm` enforces:
 *   - required fields (`name`, `customer_type`, `phone`, `email`) at the
 *     `Customer` model level;
 *   - phone format via `validate_phone_number` (libphonenumber, same engine
 *     as intl-tel-input client-side);
 *   - VAT format + checksum via `validate_vat_number` + duplicate check in
 *     `clean_vat_number`;
 *   - VAT required when `customer_type == 'company'` in `clean()`;
 *   - `payment_terms_days` ∈ [1, 365] via the model `MinValueValidator`
 *     / `MaxValueValidator`;
 *   - CONTRACTOR + INDIVIDUAL is rejected in `Customer.clean()` (PR 2.1f-a
 *     hard-block — a contractor must be a COMPANY).
 *
 * Errors come back as `.field-group.has-error` blocks via
 * `components/_field.html`, and the global `form-error-scroll` helper
 * centres the first one after the swap. The submit button is locked /
 * unlocked by the global `data-protect-submit` machinery in
 * `utils/forms/submit-button.js`. There is intentionally NO `submit` listener
 * with `e.preventDefault()` here — see `team-form.js` for the canonical
 * pattern and Migration #6 for the rationale.
 *
 * The async VAT duplicate check (debounced on `input` and `blur`) is
 * kept as live feedback so users see duplicates immediately, but it does
 * not block the submit: the server's `clean_vat_number()` is the real
 * authority.
 */

import { isValidVatNumber } from '../utils/forms/vat-validation.js';
import { initEmailValidation } from '../utils/forms/form-validation.js';
import { initPhoneInput, initAllPhoneInputs, getPhoneNumber } from '../utils/forms/phone-input.js';
import { attachBicAutoDerive } from '../utils/forms/bic-autoderive.js';
import { api, ApiError } from '../utils/api.js';
import { showToast } from '../utils/ui/toast.js';
import { reinitFlowbiteWidgets } from '../utils/ui/flowbite-reinit.js';
// PR 2.1f-c (May 2026) — the inline EndClient picker + create-modal
// was scrapped in favour of an automatic EndClient materialisation
// from the Location's own fields (see
// ``apps.office.views_create._materialise_end_client_from_location``).
// PO 2026-05-21 — the orphan ``end-client-picker.js`` module that
// had been kept lying around (no-oping silently because its modal
// shell no longer existed) was deleted in full alongside its view,
// URLs, partial and AJAX test.

// ========================================================
// VALIDATION CLEARING
// ========================================================

function initValidation(formEl) {
    if (!formEl) return;

    function clearFieldError(field) {
        if (!field) return;
        const fieldGroup = field.closest('.field-group');
        if (fieldGroup) {
            fieldGroup.classList.remove('has-error');
            var itiWrapper = field.closest('.iti');
            if (itiWrapper) {
                itiWrapper.classList.remove('iti--error');
            } else {
                field.classList.remove('border-red-500', 'dark:border-red-500');
                field.classList.add('border-gray-300', 'dark:border-gray-600');
            }
            fieldGroup.querySelectorAll('.field-error, p.text-red-600').forEach(msg => msg.remove());
        }
    }

    // If the user fills phone or email in a contact row, clear the "Phone or
    // email is required" cross-field error from the PARTNER input too, so both
    // red borders disappear together (instead of only the one being typed in).
    function clearContactCrossFieldError(field) {
        if (!field || !field.value || !field.value.trim()) return;
        var row = field.closest('.contact-form');
        if (!row) return;
        var name = field.getAttribute('name') || '';
        var partner = null;
        if (name.endsWith('-phone')) {
            partner = row.querySelector('input[name$="-email"]');
        } else if (name.endsWith('-email')) {
            partner = row.querySelector('input[name$="-phone"]');
        }
        if (!partner) return;
        var partnerFg = partner.closest('.field-group');
        if (!partnerFg) return;
        var crossErr = partnerFg.querySelector('.field-error.contact-required-error');
        if (!crossErr) return;
        crossErr.remove();
        // Only reset the partner's error styling if no OTHER error (e.g. an
        // invalid-email-format error on the email input) remains.
        if (!partnerFg.querySelector('.field-error')) {
            clearFieldError(partner);
        }
    }

    formEl.addEventListener('input', function (e) {
        const field = e.target;
        if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
            clearFieldError(field);
            clearContactCrossFieldError(field);
        }
    });

    formEl.addEventListener('change', function (e) {
        const field = e.target;
        if (field.tagName === 'SELECT' && field.value) clearFieldError(field);
        if (field.tagName === 'INPUT' && field.value.trim()) {
            clearFieldError(field);
            clearContactCrossFieldError(field);
        }
    });
}

// ========================================================
// BILLING MODE TOGGLE (REMOVED — PR-4b contract refactor, 2026-05-25)
// ========================================================
//
// The customer-level ``billing_mode`` + ``invoice_day_of_month`` UI
// was removed from the Customer form when those settings moved to
// the WorkSite (= contract) level. The dynamic show/hide of
// ``#invoice_day_field`` + the live ``#billing-mode-hint`` helper
// they drove no longer have a host element in the DOM, so the
// ``initBillingModeToggle`` initialiser was deleted in full. The
// equivalent toggle on the WorkSite form is wired by
// ``worksite-form.js`` (HTMX swap of the billing card content).
//
// ``autoInitCustomerForm`` (bottom of this file) no longer reads
// the ``data-billing-hint-*`` dataset attributes either — the
// template stopped emitting them in the same PR.

// ========================================================
// RELATIONSHIP TYPE TOGGLE (subcontracting, PR 1.4 → PR 2.1f-c)
// ========================================================
//
// PR 2.1f-c pruning:
//   - DIRECT_REQUIRED_LOCATION_FIELDS table dropped (the four address
//     fields are now required in BOTH modes; the EndClient is no
//     longer the source of truth for the site address).
//   - ``ensureSwitchWarning`` / ``countLocationsWithoutEndClient``
//     dropped — there is no longer a per-row ``end_client`` picker to
//     warn about. The EndClient is materialised silently at save
//     time by the server.
//
// Remaining responsibilities:
//   - bandeau bleu shown only in CONTRACTOR
//   - HQ billing-address card revealed + ``required`` toggled
//   - "Particulier" radio disabled in CONTRACTOR (a sous-traitant
//     must be a company; the server also enforces this in
//     Customer.clean)
//   - per-row ``[data-contractor-only]`` block (the « Référence
//     donneur d'ordre » input) revealed
//
// Rule 4 of ``.cursor/rules/hx-boost-init.mdc`` — per-element
// JS-property flag (``form._relationshipToggleBound``), NOT
// module-level. The <form> is swapped on every boosted nav so the
// flag dies with it and we correctly re-bind on the next visit.

function wireRelationshipTypeToggleOnce(form) {
    if (!form || form._relationshipToggleBound) return;

    const radios = form.querySelectorAll('input[name="customer-relationship_type"], input[name="relationship_type"]');
    if (!radios.length) return; // subcontracting_enabled = false → toggle absent

    form._relationshipToggleBound = true;

    // Smart revert of the forced Particulier→Entreprise flip (dirty
    // tracking). When CONTRACTOR mode forces customer_type to COMPANY,
    // we record that the change was SYSTEM-initiated on the form
    // (``form._ctAutoFlipped``). Leaving CONTRACTOR then undoes the
    // system's own change — back to Particulier — but ONLY while the
    // user hasn't expressed a choice themselves: any *trusted* (real
    // user) interaction with the customer_type radios cancels the
    // pending revert. ``event.isTrusted`` natively separates a human
    // click from the synthetic ``change`` we dispatch below, so a
    // user-picked Entreprise is never clobbered. Per-element flags
    // (rule 4 of hx-boost-init.mdc) — they die with the form on swap.
    form.querySelectorAll(
        'input[name="customer-customer_type"], input[name="customer_type"]'
    ).forEach(r => {
        ['click', 'change'].forEach(evName => {
            r.addEventListener(evName, (ev) => {
                if (ev.isTrusted) form._ctAutoFlipped = false;
            });
        });
    });

    const refresh = () => {
        let selected = null;
        radios.forEach(r => { if (r.checked) selected = r.value; });
        const isContractor = selected === 'contractor';

        // PR 2.1f-c follow-up (PO 2026-05-20) — the
        // ``[data-contractor-banner]`` blue banner that used
        // to live above the table was removed (redundant with
        // the « Sites d'intervention » heading + the per-row
        // « Référence donneur d'ordre » field). Nothing to
        // toggle here anymore.

        // PR 2.5 — the billing-address card is ALWAYS visible (DIRECT
        // and CONTRACTOR) and its inputs are always ``required`` (the
        // 4 mandatory billing_address_* fields). No relationship-type
        // toggle needed here anymore; the data-hq-address-card hook
        // and the conditional ``required`` loop on the old hq_* names
        // are gone with PR 2.5's unified buyer-address model.

        // PR 2.1f-c — The DIRECT-mode address block hide-in-CONTRACTOR
        // behaviour was removed when the EndClient picker was scrapped.
        // The Location modal now shows the SAME fields (label / address /
        // contact / access / parking) regardless of relationship_type
        // — the user fills the site once, and the view's save loop
        // materialises the EndClient FROM those fields. ``label`` /
        // ``address_*`` therefore stay ``required`` in both modes.

        // PR 2.1d — flip the card title so the Locations card reads
        // as "Adresses & Contacts" in DIRECT mode and "Sites
        // d'intervention" in CONTRACTOR mode.
        // PR 2.1f-c follow-up (PO 2026-05-20) — the add-button label
        // is now a neutral « + Ajouter » in both modes, so the
        // dual ``[data-add-location-label-*]`` spans were dropped
        // from the template and there is nothing to toggle here.
        form.querySelectorAll('[data-locations-card-title-direct]').forEach(el => {
            el.classList.toggle('hidden', isContractor);
        });
        form.querySelectorAll('[data-locations-card-title-contractor]').forEach(el => {
            el.classList.toggle('hidden', !isContractor);
        });

        // PR 2.1f-c follow-up (PO 2026-05-20) —
        //   1) Flip the « Libellé » / « Nom du client final »
        //      label inside the Location modal: in CONTRACTOR
        //      mode the field materialises ``end_client.name``
        //      at save time, so the label should reflect that.
        //   2) Flip the modal title (« Adresse & contact du
        //      site » → « Site d'intervention ») — the spans
        //      already exist in the template since PR 2.1f-b
        //      but were never wired up.
        // Both targets can live OUTSIDE the form (a row
        // displaced into ``#location-modal`` is no longer a
        // descendant of ``<form>``, and the modal title lives
        // in ``document.body`` directly), so we query the full
        // document instead of just the form subtree.
        document.querySelectorAll('[data-location-label-direct]').forEach(el => {
            el.classList.toggle('hidden', isContractor);
        });
        document.querySelectorAll('[data-location-label-contractor]').forEach(el => {
            el.classList.toggle('hidden', !isContractor);
        });
        document.querySelectorAll('[data-location-modal-title-direct]').forEach(el => {
            el.classList.toggle('hidden', isContractor);
        });
        document.querySelectorAll('[data-location-modal-title-contractor]').forEach(el => {
            el.classList.toggle('hidden', !isContractor);
        });

        // PR 2.1f-c follow-up (PO 2026-05-20) — first column of
        // the locations summary table swaps between « Libellé »
        // (DIRECT) and « Client final » (CONTRACTOR). The two
        // <span>s live inside the <thead> and are hidden /
        // shown here in lock-step with the modal labels above.
        document.querySelectorAll('[data-locations-table-col-direct]').forEach(el => {
            el.classList.toggle('hidden', isContractor);
        });
        document.querySelectorAll('[data-locations-table-col-contractor]').forEach(el => {
            el.classList.toggle('hidden', !isContractor);
        });

        // PO 2026-05-21 — empty-state message in the locations summary
        // table mirrors the same DIRECT/CONTRACTOR vocabulary swap as
        // the card title and the first column header above. Without
        // this, a CONTRACTOR-mode card with zero rows would still say
        // « No address yet », contradicting its own « Sites
        // d'intervention » section title.
        document.querySelectorAll('[data-locations-empty-direct]').forEach(el => {
            el.classList.toggle('hidden', isContractor);
        });
        document.querySelectorAll('[data-locations-empty-contractor]').forEach(el => {
            el.classList.toggle('hidden', !isContractor);
        });

        // PR 2.5 — billing-address marker UI (star button) and the
        // per-row badge were removed (the model field itself is
        // gone). The buyer address lives entirely on
        // Customer.billing_address_* now — no per-Location marker,
        // no star, no [data-billing-marker-only] toggle, no badge.

        // PR 2.1f-b — UI gating : a CONTRACTOR customer MUST be a
        // company. The server (Customer.clean) already rejects the
        // combo ``relationship_type=CONTRACTOR + customer_type=INDIVIDUAL``;
        // this is purely UX so the operator sees the constraint before
        // hitting the wall of red. We:
        //   - disable the "Particulier" radio (with title= tooltip);
        //   - auto-flip to "Company" if Particulier was the current
        //     pick (and dispatch ``change`` so initCustomerType's VAT
        //     visibility hook fires);
        //   - re-enable on DIRECT so the user can switch back.
        // PR 2.2-ter (PO 2026-05-21) — skip this gating entirely in
        // edit mode : ``customer_type`` is FROZEN after creation,
        // both radios are rendered server-side with ``disabled``
        // (CustomerForm.__init__), and dispatching ``change`` here
        // would either no-op (disabled inputs swallow change) or
        // worse, the unconditional ``r.disabled = false`` branch in
        // DIRECT mode would UNFREEZE the Particulier radio that the
        // server intentionally locked. Safer to short-circuit ; the
        // freeze itself + the dropped ``change`` event are both
        // benign because:
        //   * The contractor+individual server rejection in
        //     ``Customer.clean`` never fires (both fields are
        //     frozen on edit, so the COMPANY+CONTRACTOR pairing
        //     guaranteed at creation can't be flipped).
        //   * The VAT visibility hook (``initCustomerType``) re-runs
        //     once at page load with the saved ``customer_type``
        //     and pins the right visibility ; no subsequent change
        //     event is needed because the field is frozen.
        const isEditMode = form.dataset.isEdit === 'true';
        if (!isEditMode) {
            const ctRadios = form.querySelectorAll(
                'input[name="customer-customer_type"], input[name="customer_type"]'
            );
            ctRadios.forEach(r => {
                const labelEl = r.closest('label');
                if (r.value === 'individual') {
                    if (isContractor) {
                        if (r.checked) {
                            r.checked = false;
                            // Record that THIS flip is system-initiated so
                            // leaving CONTRACTOR can revert it (see the
                            // dirty-tracking block above). Set BEFORE the
                            // synthetic dispatch — isTrusted=false keeps
                            // the flag, a later real user click clears it.
                            form._ctAutoFlipped = true;
                            ctRadios.forEach(other => {
                                if (other.value === 'company') {
                                    other.checked = true;
                                    other.dispatchEvent(new Event('change', { bubbles: true }));
                                }
                            });
                        }
                        r.disabled = true;
                        r.setAttribute(
                            'title',
                            'Un sous-traitant doit être une société — le mode \u00ab Particulier \u00bb est désactivé en CONTRACTOR.'
                        );
                        // Cursor policy (cf. .cursor/rules/no-not-allowed-cursor.mdc):
                        // a disabled form CONTROL is inert, not a refused
                        // action — keep the default arrow cursor. The
                        // ``title=`` attribute carries the explain-why.
                        if (labelEl) labelEl.classList.add('opacity-50', 'cursor-default');
                    } else {
                        r.disabled = false;
                        r.removeAttribute('title');
                        if (labelEl) labelEl.classList.remove('opacity-50', 'cursor-default');
                        // Back to DIRECT: undo the system's own forced flip
                        // (and only that — a user-picked Entreprise keeps
                        // ``_ctAutoFlipped`` false and is left untouched).
                        // The synthetic ``change`` re-runs the VAT/legal-form
                        // visibility hook in initCustomerType.
                        if (form._ctAutoFlipped) {
                            form._ctAutoFlipped = false;
                            r.checked = true;
                            r.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                }
            });
        }

        // PR 2.1f-c — Reveal the single per-row CONTRACTOR-only block
        // (« Référence donneur d'ordre »). Replaces the previous
        // ``[data-end-client-section]`` picker block (dropped) and its
        // associated ``data-end-client-required`` ``*`` toggle.
        form.querySelectorAll('[data-contractor-only]').forEach(section => {
            section.classList.toggle('hidden', !isContractor);
        });
    };

    radios.forEach(r => r.addEventListener('change', refresh));
    refresh();

    // Expose for ``initLocationFormset`` so freshly-cloned rows can be
    // re-styled to match the current selected mode without having to
    // rediscover the radios. Per-form (not module-level) — survives the
    // boost-swap lifecycle alongside the form element it's attached to.
    form._refreshRelationshipToggle = refresh;
}

// ========================================================
// CUSTOMER TYPE TOGGLE (VAT number visibility)
// ========================================================

function getSelectedCustomerType() {
    const radios = document.querySelectorAll(
        'input[name="customer-customer_type"], input[name="customer_type"]'
    );
    let selected = 'individual';
    radios.forEach(r => { if (r.checked) selected = r.value; });
    return selected;
}

function initCustomerType() {
    const vatNumberField = document.getElementById('vat_number_field');
    const vatNumberInput =
        document.getElementById('id_customer-vat_number') ||
        document.getElementById('id_vat_number');
    // Legal form is a company-only field (auto-fillable from the VAT/BCE
    // lookup); it mirrors the VAT field's show/hide + clear behaviour.
    const legalFormField = document.getElementById('legal_form_field');
    const legalFormInput =
        document.getElementById('id_customer-legal_form') ||
        document.getElementById('id_legal_form');

    const customerTypeRadios = document.querySelectorAll(
        'input[name="customer-customer_type"], input[name="customer_type"]'
    );

    if (!customerTypeRadios.length || !vatNumberField) return { vatNumberField: null, vatNumberInput: null };

    // PR 2.2-quater (PO 2026-05-21 + relax follow-up same day) —
    // keep the ``preferred_payment_method`` dropdown in sync with
    // the currently-selected ``customer_type`` in CREATE mode. In
    // edit mode ``customer_type`` is frozen server-side (cf.
    // ``CustomerForm.__init__``) so no sync is needed — the
    // server-side ``choices`` filter already removed the invalid
    // entries before the page rendered. Source of truth for the
    // disallowed values lives on ``Customer.B2B_INCOMPATIBLE_
    // PREFERENCES`` (apps/customers/models.py) ; the strings
    // duplicated here MUST stay in lockstep with that frozenset.
    // Strings (not display labels) are matched against the option
    // ``value`` attribute, so they survive any translation.
    //
    // Only ``cash`` is hidden ; CARD was originally in the set but
    // was relaxed on PO request the same day — there is no statutory
    // cap on B2B card payments and Bancontact Business / Stripe /
    // Mollie are growing legitimate B2B defaults. The CASH block
    // stays because auto-pre-filling cash on a B2B invoice would
    // trip the AML cap (loi 18/09/2017 art. 67, 3 000 €) at
    // payment-recording time.
    const B2B_INCOMPATIBLE = new Set(['cash']);
    const paymentSelect =
        document.getElementById('id_customer-preferred_payment_method') ||
        document.getElementById('id_preferred_payment_method');

    function syncPaymentOptions(isCompany) {
        if (!paymentSelect) return;
        let selectedNowInvalid = false;
        paymentSelect.querySelectorAll('option').forEach(opt => {
            const restricted = B2B_INCOMPATIBLE.has(opt.value);
            // ``hidden`` keeps the option out of every dropdown :
            //   * native browser <select> (HTML spec since 2017)
            //   * the project's custom-select component (taught to
            //     respect ``<option hidden>`` in PR 2.2-quater)
            // ``disabled`` is paired in for two belt-and-braces
            // reasons : (a) screen readers / VoiceOver historically
            // surface hidden options as "dimmed" in some Safari
            // versions, ``disabled`` removes them from the AT tree ;
            // (b) hardens form submission — a crafted POST with
            // ``preferred_payment_method=cash`` would still be
            // rejected by the server (``Customer.clean`` +
            // ``CustomerForm.choices`` filter), but disabled
            // options also short-circuit submission at the browser
            // layer, which spares a server round-trip on user-agent
            // automation gone wrong.
            if (isCompany && restricted) {
                opt.hidden = true;
                opt.disabled = true;
                if (opt.selected) selectedNowInvalid = true;
            } else {
                opt.hidden = false;
                opt.disabled = false;
            }
        });
        // If the operator had picked a now-invalid value (e.g.
        // selected CASH while the radio was INDIVIDUAL, then
        // flipped to COMPANY), fall back to BANK_TRANSFER — the
        // canonical B2B default also used by the demo seed.
        if (selectedNowInvalid) {
            paymentSelect.value = 'bank';
            paymentSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    function update() {
        const isCompany = getSelectedCustomerType() === 'company';
        vatNumberField.classList.toggle('hidden', !isCompany);
        if (!isCompany && vatNumberInput) vatNumberInput.value = '';
        if (legalFormField) {
            legalFormField.classList.toggle('hidden', !isCompany);
            if (!isCompany && legalFormInput) legalFormInput.value = '';
        }
        syncPaymentOptions(isCompany);
    }

    update();
    customerTypeRadios.forEach(r => r.addEventListener('change', update));

    return { vatNumberField, vatNumberInput, legalFormInput };
}

// ========================================================
// VAT NUMBER LIVE FEEDBACK (non-blocking — server is the authority)
// ========================================================
// Live VAT feedback, all non-blocking (the server stays the authority via
// `CustomerForm.clean_vat_number`):
//   * format check (mod-97 / regex) on blur only — a half-typed number must
//     not flash an error. Takes priority over the duplicate check.
//   * async duplicate check on input (debounced) + blur, shown inline.
// Neither calls `e.preventDefault()`.

function initVatLiveFeedback(formEl, vatRefs) {
    if (!formEl || !vatRefs.vatNumberField || !vatRefs.vatNumberInput) return;
    var dupMsg = formEl.dataset.duplicateVatMessage || 'A customer with this VAT number already exists.';
    var formatMsg = formEl.dataset.invalidVatMessage || 'Invalid VAT number format.';
    var checkUrl = formEl.dataset.checkVatUrl || '';
    var excludeId = formEl.dataset.customerId || '';
    var field = vatRefs.vatNumberField;
    var input = vatRefs.vatNumberInput;
    var vatDuplicateFlag = false;
    var checkTimer = null;
    var lastCheckedValue = null;

    function showError(msg) {
        field.classList.add('has-error');
        input.classList.add('border-red-500', 'dark:border-red-500');
        input.classList.remove('border-gray-300', 'dark:border-gray-600');
        field.querySelectorAll('.field-error').forEach(function(el) { el.remove(); });
        var p = document.createElement('p');
        p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error';
        p.textContent = msg;
        input.parentNode.appendChild(p);
    }

    function clearError() {
        vatDuplicateFlag = false;
        lastCheckedValue = null;
        field.classList.remove('has-error');
        input.classList.remove('border-red-500', 'dark:border-red-500');
        input.classList.add('border-gray-300', 'dark:border-gray-600');
        field.querySelectorAll('.field-error').forEach(function(el) { el.remove(); });
    }

    function checkDuplicate() {
        var val = input.value.replace(/\s/g, '').toUpperCase();
        if (!val || !checkUrl || !isValidVatNumber(val)) return;
        if (val === lastCheckedValue) return;
        lastCheckedValue = val;

        var url = checkUrl + '?vat_number=' + encodeURIComponent(val);
        if (excludeId) url += '&exclude=' + excludeId;
        fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (input.value.replace(/\s/g, '').toUpperCase() !== val) return;
                if (!data.available) {
                    vatDuplicateFlag = true;
                    showError(dupMsg);
                }
            })
            .catch(function() {});
    }

    input.addEventListener('input', function() {
        clearError();
        if (checkTimer) clearTimeout(checkTimer);
        checkTimer = setTimeout(checkDuplicate, 500);
    });

    input.addEventListener('blur', function() {
        if (checkTimer) clearTimeout(checkTimer);
        var val = input.value.replace(/\s/g, '').toUpperCase();
        if (!val) return;
        // Format takes priority over the duplicate check: a malformed number
        // can't meaningfully be looked up. Validated on blur only (never
        // mid-typing) so a half-entered number doesn't flash an error.
        if (!isValidVatNumber(val)) {
            showError(formatMsg);
            return;
        }
        // If we already know this value is a duplicate, the form-wide
        // `initValidation` change handler has just wiped our error paragraph
        // (it clears errors on any non-empty input change). Re-assert it
        // synchronously so the user never sees the error flicker away.
        if (vatDuplicateFlag && val === lastCheckedValue) {
            showError(dupMsg);
            return;
        }
        checkDuplicate();
    });
}

// ========================================================
// COMPANY LOOKUP (auto-fill from the Belgian VAT/BCE registry)
// ========================================================
//
// Auto-fills the target fields (name, legal form, billing address) from the
// registry, overwriting any value already typed. When the VAT already belongs
// to an existing customer the registry is NOT queried at all (saves an
// external call) and the banner stays silent — the duplicate is surfaced by
// ``initVatLiveFeedback``. The country select is intentionally never touched.
// Any API failure degrades silently — it never blocks the save (the server
// endpoint mirrors this).
function initCompanyLookup(formEl, vatRefs) {
    if (!formEl || !vatRefs || !vatRefs.vatNumberInput) return;
    const lookupUrl = formEl.dataset.companyLookupUrl || '';
    if (!lookupUrl) return;

    const vatInput = vatRefs.vatNumberInput;
    // Edit mode freezes the VAT number (disabled) — no lookup to run.
    if (vatInput.disabled) return;

    const banner = document.getElementById('company-lookup-banner');
    if (!banner) return;

    // Used to skip the external registry call entirely when the VAT already
    // belongs to an existing customer (the duplicate is surfaced separately by
    // ``initVatLiveFeedback``). Reuses the same internal check-vat endpoint.
    const checkVatUrl = formEl.dataset.checkVatUrl || '';
    const excludeId = formEl.dataset.customerId || '';

    const i18n = {
        loading: formEl.dataset.companyLookupLoading || 'Looking up company…',
        found: formEl.dataset.companyLookupFound || 'Company found in the registry.',
        notFound: formEl.dataset.companyLookupNotFound || 'No company found for this VAT number.',
    };

    function getInput(key) {
        return (
            document.getElementById('id_customer-' + key) ||
            document.getElementById('id_' + key)
        );
    }

    // key -> form input. Order drives the banner's conflict list.
    const fieldInputs = {
        name: getInput('name'),
        legal_form: getInput('legal_form'),
        billing_address_line1: getInput('billing_address_line1'),
        billing_postal_code: getInput('billing_postal_code'),
        billing_city: getInput('billing_city'),
    };

    function hideBanner() {
        banner.classList.add('hidden');
        banner.textContent = '';
    }

    function showMessage(text, tone) {
        banner.textContent = '';
        banner.classList.remove('hidden');
        const p = document.createElement('p');
        p.textContent = text;
        p.className =
            tone === 'error'
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-green-600 dark:text-green-400 font-medium';
        banner.appendChild(p);
    }

    function fillInput(input, value) {
        input.value = value;
        // Notify the form-wide validation / dirty-state handlers.
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function renderResult(fields) {
        banner.textContent = '';
        banner.classList.remove('hidden');

        const header = document.createElement('p');
        header.className = 'text-green-600 dark:text-green-400 font-medium';
        header.textContent = i18n.found;
        banner.appendChild(header);

        // Auto-replace every target field with the registry value, even when
        // the operator already typed something. (Duplicate VATs never reach
        // here — runLookup short-circuits before the registry is queried.)
        Object.keys(fieldInputs).forEach((key) => {
            const input = fieldInputs[key];
            const suggested = (fields[key] || '').trim();
            if (!input || !suggested) return;
            if ((input.value || '').trim() === suggested) return;
            fillInput(input, suggested);
        });
    }

    // Resolves to true when a customer already owns this VAT number. Failures
    // resolve to false so a flaky check-vat never blocks the address pre-fill.
    function isDuplicateVat(raw) {
        if (!checkVatUrl) return Promise.resolve(false);
        let url = checkVatUrl + '?vat_number=' + encodeURIComponent(raw);
        if (excludeId) url += '&exclude=' + excludeId;
        return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then((r) => r.json())
            .then((d) => d && d.available === false)
            .catch(() => false);
    }

    let lastLookedUp = null;
    let debounceTimer = null;

    function runLookup() {
        const raw = (vatInput.value || '').replace(/\s/g, '').toUpperCase();
        // Only Belgian numbers, and only once they look structurally valid.
        if (!raw || !raw.startsWith('BE') || !isValidVatNumber(raw)) {
            // Reset the dedup memory so clearing the VAT (or making it
            // invalid) lets the SAME number trigger a fresh lookup again.
            // Without this, re-typing the exact VAT the operator just wiped
            // — after also clearing some auto-filled fields — short-circuits
            // on the ``raw === lastLookedUp`` guard and never re-fills.
            lastLookedUp = null;
            hideBanner();
            return;
        }
        if (raw === lastLookedUp) return;
        lastLookedUp = raw;

        // Check the (cheap, internal) duplicate endpoint FIRST: if a customer
        // already owns this VAT there is nothing to create, so we skip the
        // external registry call entirely (saves a billed/rate-limited request)
        // and stay silent — the duplicate warning is surfaced by
        // ``initVatLiveFeedback``.
        isDuplicateVat(raw).then((isDup) => {
            if ((vatInput.value || '').replace(/\s/g, '').toUpperCase() !== raw) return;
            if (isDup) {
                hideBanner();
                return;
            }

            showMessage(i18n.loading, 'error');

            fetch(lookupUrl + '?vat=' + encodeURIComponent(raw), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then((r) => r.json())
                .then((data) => {
                    // Ignore stale responses if the field changed meanwhile.
                    const now = (vatInput.value || '').replace(/\s/g, '').toUpperCase();
                    if (now !== raw) return;
                    if (data && data.found) {
                        renderResult(data.fields || {});
                    } else if (data && data.reason === 'not_found') {
                        showMessage(i18n.notFound, 'error');
                    } else {
                        // unsupported_country / unavailable → stay silent.
                        hideBanner();
                    }
                })
                .catch(() => {
                    // Network/parse failure must never block the form.
                    hideBanner();
                });
        });
    }

    vatInput.addEventListener('input', () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(runLookup, 600);
    });
    vatInput.addEventListener('blur', () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        runLookup();
    });
}

// ========================================================
// LOCATION FORMSET MANAGEMENT
// ========================================================

// PR 2.1f-b — Location formset rendered as compact TABLE + per-row MODAL.
//
// Data model:
//   * ``#locations-container`` is now ``hidden`` and acts as a holding
//     pen for the formset rows. The rows are still inputs with the
//     ``locations-N-…`` naming the Django formset needs, so submission
//     just works — wherever the inputs sit at submit-time.
//   * ``#locations-table-body`` displays one summary ``<tr>`` per
//     row, generated at init from the holding pen and refreshed on
//     every modal close. Click "Modifier" → the row's DOM node is
//     MOVED into the modal-content slot. Click "Fermer/Valider" →
//     the node is moved BACK to the holding pen and the table row is
//     refreshed from the (possibly mutated) inputs.
//   * "+ Ajouter" clones the empty_form template, increments
//     TOTAL_FORMS, appends to the holding pen, then immediately opens
//     the modal on the new row.
//
// Deletion handling (Django formset contract):
//   * existing-row (has ``-id``):   set ``-DELETE=on`` hidden input,
//     keep the row in the holding pen so the formset receives the
//     delete marker; mark the table row as struck-through with an
//     "Annuler" link to flip the marker back off.
//   * new-row (no ``-id``):         set ``-DELETE=on`` AND drop the
//     summary table row entirely; the holding pen still gets the
//     marker so TOTAL_FORMS stays consistent.
//
// On first paint we also fix the legacy server-side ``DELETE`` rows
// (a row that came back with the box ticked after a server-side
// re-render) so the table reflects that state.

const LOCATION_MODAL_ID = 'location-modal';
const LOCATION_HOLDING_PEN_ID = 'locations-container';
const LOCATION_TABLE_BODY_ID = 'locations-table-body';
const LOCATION_ROW_TEMPLATE_ID = 'locations-table-row-template';

function _getLocationModalNodes() {
    return {
        modal: document.getElementById(LOCATION_MODAL_ID),
        content: document.querySelector('[data-location-modal-content]'),
        holdingPen: document.getElementById(LOCATION_HOLDING_PEN_ID),
        tableBody: document.getElementById(LOCATION_TABLE_BODY_ID),
        tableEmpty: document.querySelector('[data-locations-table-empty]'),
        rowTemplate: document.getElementById(LOCATION_ROW_TEMPLATE_ID),
    };
}

function _isRowDeleted(rowEl) {
    const delInput = rowEl.querySelector('input[name$="-DELETE"]');
    if (!delInput) return false;
    if (delInput.type === 'checkbox') return delInput.checked;
    return delInput.value === 'on';
}

function _isRowNew(rowEl) {
    const idInput = rowEl.querySelector('input[name$="-id"]');
    return !idInput || !idInput.value;
}

function _setRowDeleted(rowEl, deleted) {
    let delInput = rowEl.querySelector('input[name$="-DELETE"]');
    if (!delInput) {
        const anyInput = rowEl.querySelector('input[name^="locations-"], select[name^="locations-"], textarea[name^="locations-"]');
        if (!anyInput) return;
        const m = anyInput.name.match(/^locations-(\d+)-/);
        if (!m) return;
        delInput = document.createElement('input');
        delInput.type = 'hidden';
        delInput.name = 'locations-' + m[1] + '-DELETE';
        rowEl.appendChild(delInput);
    }
    if (delInput.type === 'checkbox') delInput.checked = deleted;
    else delInput.value = deleted ? 'on' : '';
}

function _readRowValues(rowEl) {
    const getRaw = (suffix) => {
        const el = rowEl.querySelector('[name$="-' + suffix + '"]');
        return el ? (el.value || '').trim() : '';
    };
    return {
        label: getRaw('label'),
        address_line1: getRaw('address_line1'),
        city: getRaw('city'),
        postal_code: getRaw('postal_code'),
        contact_name: getRaw('contact_name'),
        contact_phone: getRaw('contact_phone'),
        client_reference: getRaw('client_reference'),
    };
}

// PR 2.5 — ``_isBillingRow`` removed. The per-row billing marker
// (``Location.is_billing_address``) is gone; the customer's billing
// address now lives on ``Customer.billing_address_*`` exclusively.

// PR 2.2-bis — soft-archive helpers (``is_active`` round-trips
// through the hidden ``locations-N-is_active`` input rendered by
// ``LocationForm``'s ``HiddenInput`` widget).
//
// Default semantics : a row WITHOUT the input (defensive ; the
// template always renders it) is treated as ACTIVE, so a malformed
// DOM never accidentally archives an existing row at submit time.
function _isRowActive(rowEl) {
    const inp = rowEl.querySelector('input[name$="-is_active"]');
    if (!inp) return true;
    const v = (inp.value || '').trim().toLowerCase();
    // Django ``HiddenInput`` for a ``BooleanField`` renders the
    // initial value as ``"True"`` / ``"False"`` (the bool's
    // ``__str__``). When the operator clicks « Archiver » the JS
    // rewrites the value to the empty string, which Django's
    // ``BooleanField.to_python`` then reads as False — same
    // contract as an unchecked checkbox. ``"on"`` / ``"1"`` /
    // ``"true"`` are kept as truthy values for defensive parity
    // with the billing helper above (form widgets evolve).
    return v === 'true' || v === '1' || v === 'on';
}

function _setRowActiveFlag(rowEl, active) {
    let inp = rowEl.querySelector('input[name$="-is_active"]');
    if (!inp) {
        const anyInput = rowEl.querySelector('input[name^="locations-"], select[name^="locations-"], textarea[name^="locations-"]');
        if (!anyInput) return;
        const m = anyInput.name.match(/^locations-(\d+)-/);
        if (!m) return;
        inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = 'locations-' + m[1] + '-is_active';
        inp.setAttribute('data-archive-flag-input', 'true');
        rowEl.appendChild(inp);
    }
    inp.value = active ? 'True' : 'False';
    // Mirror onto the data-attribute so subsequent rebuildTable() calls
    // see the post-toggle state without a server round-trip. The
    // ``data-can-archive`` / ``data-can-reactivate`` flags are
    // re-derived from this in ``_wireArchiveButtons``.
    rowEl.dataset.isActive = active ? 'true' : 'false';
}

// PR 2.5 — ``_setBillingRowFlag`` removed (per-row marker is gone).

function _renderRowSummary(rowEl, tr, mode) {
    const v = _readRowValues(rowEl);
    const isContractor = mode === 'contractor';
    const primary = tr.querySelector('[data-cell="primary"]');
    const reference = tr.querySelector('[data-cell="reference"]');
    const address = tr.querySelector('[data-cell="address"]');
    const contactName = tr.querySelector('[data-cell="contact-name"]');
    const contactPhone = tr.querySelector('[data-cell="contact-phone"]');
    const errCell = tr.querySelector('[data-cell="errors"]');

    // PR 2.1f-c follow-up (PO 2026-05-20) — the table now has 4
    // columns : « Libellé / Client final · Adresse · Contact ·
    // Actions ». The first column shows the row's ``label``
    // field (which materialises ``end_client.name`` in
    // CONTRACTOR mode, cf. ``_materialise_end_client_from_
    // location``) regardless of mode. The CONTRACTOR-only
    // « Référence donneur d'ordre » sits under the name as a
    // hint, since it's the secondary identifier on invoices.
    primary.textContent = v.label || '— Nouvelle adresse —';
    if (isContractor && v.client_reference) {
        reference.textContent = 'Réf. : ' + v.client_reference;
        reference.classList.remove('hidden');
    } else {
        reference.textContent = '';
        reference.classList.add('hidden');
    }
    // PO 2026-05-20 — render addresses in the canonical BE postal
    // convention « Rue + numéro, Code postal Ville » (e.g.
    // « Boulevard Général Jacques 263, 1050 Ixelles ») so the
    // table mirrors what's printed on the invoice PDF. The earlier
    // dashboard-style ``·`` separator put an artificial mark
    // between postal and city, which never matches an envelope
    // address or an UBL/Peppol ``<cbc:PostalZone> <cbc:CityName>``
    // pair. The « postal city » block stays unsplit on purpose —
    // « 1050 Ixelles » is one indivisible token in BE/FR/NL.
    let addressText = '—';
    if (v.address_line1 || v.postal_code || v.city) {
        const cityBlock = [v.postal_code, v.city].filter(Boolean).join(' ');
        const parts = [v.address_line1, cityBlock].filter(Boolean);
        addressText = parts.join(', ');
    }
    address.textContent = addressText;
    contactName.textContent = v.contact_name || '—';
    // PO 2026-05-20 — display the phone in E.164 (with country
    // prefix, e.g. ``+32479763567``) so the operator sees the same
    // canonical international format the invoice / app uses. Raw
    // ``v.contact_phone`` is the local-format input value
    // (« 479 76 35 67 ») which is missing the dial code and looks
    // truncated in the table. We pull the formatted number from
    // the iti instance bound to the row's phone <input> — the
    // helper is idempotent and falls back to the raw value if
    // intl-tel-input couldn't parse it (defensive : legacy rows
    // saved as raw national format without a region hint).
    let phoneText = '';
    const phoneInp = rowEl.querySelector('input[data-phone-input]');
    if (phoneInp) {
        try {
            const iti = initPhoneInput(phoneInp);
            const formatted = (getPhoneNumber(iti) || '').trim();
            if (formatted) phoneText = formatted;
        } catch { /* fall back below */ }
    }
    contactPhone.textContent = phoneText || v.contact_phone || '';

    // PR 2.5 — billing-address badge + star icon block removed
    // entirely. The per-row marker (``Location.is_billing_address``)
    // no longer exists; the customer's billing address lives on
    // ``Customer.billing_address_*`` on its own card. ``mode`` and
    // ``isContractor`` are kept here because they still drive the
    // « Référence donneur d'ordre » sub-line above.

    const hasErrors = !!rowEl.querySelector('.field-error');
    errCell.classList.toggle('hidden', !hasErrors);
    if (hasErrors) errCell.textContent = '⚠ Erreurs à corriger';

    if (_isRowDeleted(rowEl)) {
        tr.classList.add('opacity-50', 'line-through');
    } else {
        tr.classList.remove('opacity-50', 'line-through');
    }

    // PR 2.2-bis — archived rows render with a softer dim so the
    // operator can tell at a glance which addresses are out of the
    // active picker pool. We deliberately don't apply ``line-
    // through`` here (reserved for the DELETE state) — an archived
    // row is still « real », just not pickable for new Jobs.
    if (!_isRowDeleted(rowEl) && !_isRowActive(rowEl)) {
        tr.classList.add('opacity-60', 'bg-gray-50', 'dark:bg-gray-800/50');
    } else {
        tr.classList.remove('opacity-60', 'bg-gray-50', 'dark:bg-gray-800/50');
    }
}

// PR 2.2-bis — wire the « Archiver » / « Réactiver » action buttons.
// Called once per cloned <tr> from ``rebuildTable()``. Reads the
// row's data-attributes (stamped by ``BaseLocationFormSet.annotate_
// for_archive_ui``) to decide which button to show and whether to
// disable it.
//
// PR 2.2-bis follow-up #3 (PO 2026-05-20) — explain-why text is now
// carried by a Flowbite tooltip per the project rule
// ``.cursor/rules/flowbite-tooltips.mdc``. The original implementation
// stamped a native ``btn.title = '…'`` because per-row unique IDs
// across cloned tooltip divs felt expensive ; in practice that broke
// the rule (native title = ~700 ms OS delay, no theming, invisible
// on touch). Migration : the row template now ships three
// ``<div role="tooltip" id="tooltip-{archive,reactivate,delete}-PLACEHOLDER">``
// siblings of the matching buttons, and ``_rewireRowTooltips`` below
// rewrites the placeholder IDs to ``…-<rowKey>`` then asks
// ``reinitFlowbiteWidgets`` to wire fresh Tooltip + Popper instances
// on the cloned subtree. ``rowKey`` comes from
// ``tr.dataset.locationRow`` which ``rebuildTable`` already stamps
// uniquely per row (cf. that function's per-row ``row-<idx>-<rand>``
// dataset write).
function _rewireRowTooltips(tr) {
    const rowKey = tr.dataset.locationRow;
    if (!rowKey) return;
    // FIVE tooltip divs, five buttons — paired by data-action /
    // [data-{archive,reactivate,delete,edit,star}-tooltip]. We
    // rewrite both the trigger's ``data-tooltip-target`` and the
    // sibling div's ``id`` so they match the per-row unique id.
    //
    // PR 2.2-bis follow-up #4 (PO 2026-05-20) — extended from the
    // original 3-pair list to also cover Edit (« Modifier ») and
    // Star (« Utiliser/Adresse de facturation actuelle »). Both
    // were still using native ``aria-label``-only tooltips, which
    // violated ``.cursor/rules/flowbite-tooltips.mdc`` (the
    // browser-default title-bubble has a ~700 ms OS delay, no
    // theming, no rich content, and is invisible on touch
    // devices). Star is dynamic (two states) so its tooltip text
    // is refreshed by ``_renderRowSummary``; Edit is static so
    // the template default (« Modifier ») is enough.
    const pairs = [
        { action: 'archive-row', tooltipAttr: 'data-archive-tooltip', prefix: 'tooltip-archive' },
        { action: 'reactivate-row', tooltipAttr: 'data-reactivate-tooltip', prefix: 'tooltip-reactivate' },
        { action: 'delete-row', tooltipAttr: 'data-delete-tooltip', prefix: 'tooltip-delete' },
        { action: 'edit-row', tooltipAttr: 'data-edit-tooltip', prefix: 'tooltip-edit' },
        // PR 2.5 — ``set-billing-row`` star button removed.
    ];
    pairs.forEach(({ action, tooltipAttr, prefix }) => {
        const btn = tr.querySelector(`[data-action="${action}"]`);
        const tip = tr.querySelector(`[${tooltipAttr}]`);
        if (!btn || !tip) return;
        const uniqueId = `${prefix}-${rowKey}`;
        btn.setAttribute('data-tooltip-target', uniqueId);
        tip.id = uniqueId;
    });
}

// Update a row's tooltip text via its ``[data-tooltip-content]``
// span. The Flowbite Tooltip instance reads the div's textContent
// lazily on every show, so updating the inner span is enough — no
// need to re-init the Tooltip instance for a content change.
function _setRowTooltipText(tr, tooltipAttr, text) {
    const tip = tr.querySelector(`[${tooltipAttr}]`);
    if (!tip) return;
    const content = tip.querySelector('[data-tooltip-content]');
    if (content) content.textContent = text || '';
}

function _wireArchiveButtons(rowEl, tr, rebuildTable, activeRowCount, usableRowCount) {
    const archiveBtn = tr.querySelector('[data-action="archive-row"]');
    const reactivateBtn = tr.querySelector('[data-action="reactivate-row"]');
    const deleteBtn = tr.querySelector('[data-action="delete-row"]');
    const editBtn = tr.querySelector('[data-action="edit-row"]');
    // PR 2.5 — ``starBtn`` (set-billing-row) removed; no more per-row marker.
    if (!archiveBtn || !reactivateBtn || !deleteBtn) return;

    // Rewrite placeholder tooltip IDs → per-row unique IDs. MUST run
    // BEFORE ``reinitFlowbiteWidgets(tr)`` at the bottom of this
    // function, otherwise every row's PLACEHOLDER id would collide
    // (Flowbite uses ``getElementById`` to resolve targets, so a
    // second row would silently end up pointing at the first row's
    // tooltip div — the rule has a dedicated bullet on this exact
    // collision in flowbite-tooltips.mdc § 3 « id MUST be unique »).
    _rewireRowTooltips(tr);

    const isPersisted = rowEl.dataset.isPersisted === 'true';
    const isActive = _isRowActive(rowEl);

    // PR 2.2-bis follow-up (PO 2026-05-20) — on an archived row the
    // operator's ONLY meaningful action is Reactivate ; the rest
    // (star = set as billing, pencil = edit fields) target picker
    // state and form state that archived rows are deliberately
    // excluded from. Hiding them keeps the row visually quiet (the
    // dimmed bg already screams « not in play ») and prevents
    // confusion like « I clicked edit on an archived row, why
    // didn't anything save? ». The trash icon is exposed below
    // through its own ``can_delete`` gating because hard-deleting
    // a row that's already archived is the natural cleanup path
    // when the operator is sure they want to remove it for good
    // (and the server still enforces PROTECT on Job FKs).
    if (editBtn) editBtn.classList.toggle('hidden', !isActive);
    // PR 2.5 — star button + ``isBilling`` archive gate removed.
    // The only archive gate left is "last active row" (computed
    // below).
    // PR 2.2-bis — ``activeRowCount`` is the live count of active
    // persisted rows recomputed by ``rebuildTable`` on every pass,
    // so the « last active » block stays accurate as the operator
    // archives / reactivates rows in the SAME session without a
    // page reload (the GET-time ``data-is-only-active`` would lie
    // after the first toggle). If exactly 1 active row remains
    // AND this is it, its own archive button is disabled.
    const isOnlyActive = (
        isActive && isPersisted && typeof activeRowCount === 'number'
        && activeRowCount <= 1
    );
    const hasJobs = rowEl.dataset.hasJobs === 'true';

    // Hide both buttons entirely for not-yet-saved rows — the
    // archive concept is meaningful only on persisted Locations
    // (you can't « archive » something that doesn't exist yet ;
    // the trash icon already covers the « I changed my mind »
    // path for fresh rows by discarding them outright).
    if (!isPersisted) {
        archiveBtn.classList.add('hidden');
        reactivateBtn.classList.add('hidden');
    } else if (isActive) {
        archiveBtn.classList.remove('hidden');
        reactivateBtn.classList.add('hidden');
        // PR 2.5 — only "last active row" blocks archiving now
        // (was: ``isBilling || isOnlyActive``).
        archiveBtn.disabled = isOnlyActive;
        // PR 2.2-bis / PR 2.5 — tooltip falls back to the plain
        // « Archive » verb when the button is enabled, or to the
        // « last active row » block reason when not. The
        // « billing-row » block reason variant is gone.
        const archiveText = isOnlyActive
            ? (rowEl.dataset.archiveBlockReasonOnlyActive || '')
            : (rowEl.dataset.tooltipArchiveDefault || 'Archive');
        _setRowTooltipText(tr, 'data-archive-tooltip', archiveText);
    } else {
        archiveBtn.classList.add('hidden');
        reactivateBtn.classList.remove('hidden');
        reactivateBtn.disabled = false;
        _setRowTooltipText(
            tr,
            'data-reactivate-tooltip',
            rowEl.dataset.tooltipReactivateDefault || 'Reactivate',
        );
    }

    // PR 2.2-bis — hard-delete gating. The data-can-delete flag
    // already reflects the « no Jobs » server-side computation ;
    // we also block delete when the row is archived (the operator
    // should reactivate first, then re-delete if truly intended,
    // to avoid a surprise « I clicked archive and it disappeared »
    // flow). The DELETE button itself stays for fresh-unsaved rows
    // (they have no pk, so ``data-has-jobs=false`` is always true).
    //
    // PR 2.2-bis / PR 2.5 — hard-delete gating on the trash icon:
    //   (a) row has Jobs (server-side ``can_delete=false``) → FK
    //       PROTECT would 500; surface the block up-front.
    //   (b) last usable row → would orphan the customer with zero
    //       addresses; ``BaseLocationFormSet.clean`` rule would
    //       reject at submit, so catch here.
    // The pre-PR-2.5 (★ billing) block is gone.
    const hasJobsBlock = rowEl.dataset.canDelete !== 'true' && isPersisted;
    const isLastUsableBlock = (
        typeof usableRowCount === 'number' && usableRowCount <= 1
    );
    const canDelete = !hasJobsBlock && !isLastUsableBlock;
    deleteBtn.disabled = !canDelete;
    let deleteTooltip;
    if (hasJobsBlock) {
        deleteTooltip = rowEl.dataset.deleteBlockReason || '';
    } else if (isLastUsableBlock) {
        deleteTooltip = rowEl.dataset.deleteBlockReasonLast || '';
    } else {
        deleteTooltip = rowEl.dataset.tooltipDeleteDefault || 'Delete';
    }
    _setRowTooltipText(tr, 'data-delete-tooltip', deleteTooltip);

    // PR 2.2-bis follow-up #3 — register Flowbite Tooltip + Popper
    // instances on this row's tooltip divs now that (a) their IDs
    // are unique per row and (b) the text content reflects the
    // current state. ``reinitFlowbiteWidgets`` is idempotent
    // (Flowbite's constructors register with ``override: true``)
    // so re-running on subsequent ``rebuildTable`` passes replaces
    // the previous instances without leaking listeners. We scope
    // the re-init to the row's ``<tr>`` so we don't touch any
    // other Flowbite widget on the page (the helper's contract
    // documented in ``utils/ui/flowbite-reinit.js``).
    reinitFlowbiteWidgets(tr);

    // PR 2.2-bis follow-up (PO 2026-05-20) — archive / unarchive
    // now POST to a dedicated JSON endpoint and persist the toggle
    // IMMEDIATELY rather than waiting for « Enregistrer les
    // modifications » on the parent form. Without this, the
    // operator would archive a row, switch tabs, and find the row
    // back to active on return — the archive lived only in the
    // unsaved formset state. Persisting on click matches every
    // other archive action in the office area (worksite_archive,
    // coda_archive, consumable_toggle) and removes the silent
    // « refresh wipes my archive » footgun.
    //
    // The local ``_setRowActiveFlag`` call stays — it keeps the
    // hidden ``locations-N-is_active`` input in sync so a
    // subsequent « Save changes » on the parent form re-asserts
    // the same value (the formset clean re-validates against the
    // already-persisted state, idempotently). ``rebuildTable``
    // refreshes the entire table because archiving may un-block /
    // re-block the « last active » rule on OTHER rows (e.g.
    // archiving row 1 of 3 leaves row 2 + row 3 archive-eligible ;
    // archiving row 2 of 2 leaves the last active as
    // non-archivable).
    //
    // Failure handling : the ``api()`` helper already paints an
    // error toast (server-rejected POST = stale UI = e.g. another
    // tab made this row the new billing target). We deliberately
    // do NOT flip the local state on failure — the DB stayed put
    // and the UI must match. Re-clicking is the operator's path
    // to retry once the UI is refreshed.
    // PR 2.2-bis follow-up #6 (PO 2026-05-21) — toast strings come
    // from the parent ``<form>``'s ``data-location-archived`` /
    // ``data-location-unarchived`` attrs (rendered by Django via
    // ``{% trans %}``) so the operator sees the toast in their own
    // locale. Hardcoding text here previously bypassed the .po
    // chain — every non-default locale saw the literal even when
    // the surrounding form was fully localised. Resolving the form
    // once per closure (rather than on every click) keeps the hot
    // path tight ; ``rowEl.closest('form')`` is O(depth), and the
    // form node is stable for the lifetime of the row. Fallbacks
    // are English (= the msgid source language) so a stray legacy
    // template that forgot the data-attr still produces a useful
    // toast instead of « undefined ».
    const formEl = rowEl.closest('form');
    const archivedToast = (formEl && formEl.dataset.locationArchived) || 'Address archived.';
    const unarchivedToast = (formEl && formEl.dataset.locationUnarchived) || 'Address reactivated.';
    archiveBtn.addEventListener('click', async () => {
        if (archiveBtn.disabled) return;
        const url = rowEl.dataset.archiveUrl;
        if (!url) {
            // Defensive : a row without an archive-url is an
            // unsaved row, and the archive button is hidden for
            // unsaved rows above. If we get here it's a bug ;
            // no-op rather than persisting nothing to nowhere.
            return;
        }
        archiveBtn.disabled = true;
        try {
            const data = await api(url, { method: 'POST' });
            if (data && data.ok) {
                _setRowActiveFlag(rowEl, false);
                rebuildTable();
                try { showToast(archivedToast, 'success'); } catch { /* no-op */ }
            }
        } catch (err) {
            // ``api()`` already showed the server's error message.
            // Re-enable the button so the operator can retry once
            // they've refreshed the offending input (star marker,
            // etc.). We swallow the ApiError instance — no
            // further action needed here.
            void (err instanceof ApiError);
        } finally {
            archiveBtn.disabled = false;
        }
    });
    reactivateBtn.addEventListener('click', async () => {
        if (reactivateBtn.disabled) return;
        const url = rowEl.dataset.unarchiveUrl;
        if (!url) return;
        reactivateBtn.disabled = true;
        try {
            const data = await api(url, { method: 'POST' });
            if (data && data.ok) {
                _setRowActiveFlag(rowEl, true);
                rebuildTable();
                try { showToast(unarchivedToast, 'success'); } catch { /* no-op */ }
            }
        } catch (err) {
            void (err instanceof ApiError);
        } finally {
            reactivateBtn.disabled = false;
        }
    });
}

function _currentRelationshipMode(formEl) {
    const checked = formEl.querySelector(
        'input[name="customer-relationship_type"]:checked, input[name="relationship_type"]:checked'
    );
    return checked && checked.value === 'contractor' ? 'contractor' : 'direct';
}

function initLocationFormset() {
    const addBtn = document.getElementById('add-location-btn');
    const { modal, content, holdingPen, tableBody, tableEmpty, rowTemplate } = _getLocationModalNodes();
    const template = document.getElementById('empty-location-form');
    const totalForms = document.querySelector('input[name="locations-TOTAL_FORMS"]');

    if (!addBtn || !holdingPen || !tableBody || !rowTemplate || !template || !totalForms || !modal || !content) return;

    const formEl = addBtn.closest('form');
    if (!formEl) return;

    // PR 2.1f-c follow-up (PO 2026-05-20):
    //   Bug #1 — clicking "+ Ajouter une adresse" and then closing
    //   the modal without entering anything was leaving a phantom
    //   row in the table. Fix: defer adding the row to the table
    //   until the user explicitly clicks "Valider". Track which
    //   rows are still in their "first-open, never-confirmed" state
    //   via ``rowEl._isFreshUnsaved``, and on cancel mark them as
    //   ``DELETE=on`` so the formset save loop ignores them (the
    //   ``DeleteAwareFormMixin`` short-circuits full_clean for
    //   deleted rows — no spurious "field required" errors on
    //   an empty discarded row).
    //
    //   Bug #2 — clicking "Valider" with empty required fields did
    //   nothing visible; the row silently slipped through and the
    //   server bounced the POST. Fix: run a client-side required-
    //   field check on Valider. If any required input is empty,
    //   paint inline ``.field-error`` messages and KEEP THE MODAL
    //   OPEN so the office can fix the row right where they are.
    //
    //   Cancel-on-edit symmetry — snapshot every input value on
    //   open so "Annuler" (close) on an EXISTING row reverts any
    //   edits the user made during this modal session. Fresh rows
    //   skip the snapshot — they're discarded outright.

    function _formInputs(rowEl) {
        return rowEl.querySelectorAll(
            'input:not([type=hidden]):not([type=button]):not([type=submit]),'
            + ' select, textarea'
        );
    }

    function _snapshotValues(rowEl) {
        const snap = new Map();
        _formInputs(rowEl).forEach(el => {
            if (el.type === 'checkbox' || el.type === 'radio') {
                snap.set(el, el.checked);
            } else {
                snap.set(el, el.value);
            }
        });
        return snap;
    }

    function _restoreValues(rowEl, snap) {
        if (!snap) return;
        _formInputs(rowEl).forEach(el => {
            if (!snap.has(el)) return;
            const prev = snap.get(el);
            if (el.type === 'checkbox' || el.type === 'radio') {
                el.checked = !!prev;
            } else {
                el.value = prev;
            }
        });
    }

    function _clearRequiredErrors(rowEl) {
        rowEl.querySelectorAll('.client-required-error').forEach(p => p.remove());
        rowEl.querySelectorAll('.field-group.has-error').forEach(fg => {
            // Only drop the has-error class if the ONLY remaining error
            // came from our own client-side paragraph. Server-rendered
            // ``.field-error`` paragraphs (still present after a 422)
            // keep their group highlighted.
            if (!fg.querySelector('.field-error')) {
                fg.classList.remove('has-error');
            }
        });
    }

    function _validateModalRow(rowEl) {
        _clearRequiredErrors(rowEl);
        let firstInvalid = null;
        const requiredEls = rowEl.querySelectorAll(
            'input[required]:not([type=hidden]), select[required], textarea[required]'
        );
        requiredEls.forEach(el => {
            const value = (el.value || '').trim();
            if (value) return;
            const fg = el.closest('.field-group');
            if (!fg) return;
            fg.classList.add('has-error');
            const p = document.createElement('p');
            p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error client-required-error';
            p.textContent = 'Ce champ est obligatoire.';
            fg.appendChild(p);
            if (!firstInvalid) firstInvalid = el;
        });
        if (firstInvalid) {
            try { firstInvalid.focus(); } catch { /* */ }
            return false;
        }
        return true;
    }

    function openModalFor(rowEl) {
        // Move the row's DOM node into the modal-content slot. The
        // ``<form>`` ancestor is shared so input names + values keep
        // working for submission while displaced.
        content.innerHTML = '';
        content.appendChild(rowEl);
        rowEl._displaced = true;
        // Snapshot the current values so "Annuler" on an existing
        // row reverts any in-modal edits. Fresh rows skip the snap;
        // they're outright discarded on cancel.
        if (!rowEl._isFreshUnsaved) {
            rowEl._valueSnapshot = _snapshotValues(rowEl);
        }
        modal.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
        // Re-run the relationship-type toggle so the newly-displayed
        // row reflects the current mode (reveals the « Référence
        // donneur d'ordre » block in CONTRACTOR, etc.).
        if (typeof formEl._refreshRelationshipToggle === 'function') {
            formEl._refreshRelationshipToggle();
        }
        // PR 2.5 — the « Facturation » badge that used to sit next
        // to the modal title is gone (the per-row marker on
        // Location was removed; the billing address lives on
        // Customer.billing_address_* on its own card).
        // PR 2.1f-f follow-up (PO 2026-05-20) — upgrade every
        // ``<select.form-select>`` inside the freshly-displayed row
        // (Country picker today) to the project's custom-select
        // combobox so the modal stops rendering the browser-default
        // styling. ``initCustomSelects`` is idempotent (it tears down
        // any existing wrapper before rebuilding) so calling it on
        // every modal open is safe even when a previously-displaced
        // row is re-opened. Scoped to ``content`` so we don't
        // accidentally rebuild every other combobox on the page.
        if (typeof window.initCustomSelects === 'function') {
            try { window.initCustomSelects(content); } catch { /* */ }
        }
        // Focus the first focusable input for keyboard accessibility.
        const focusable = rowEl.querySelector('input:not([type="hidden"]), select, textarea, button');
        if (focusable) try { focusable.focus(); } catch { /* */ }
    }

    function _returnRowToPen() {
        const rowEl = content.querySelector('.location-form');
        if (!rowEl) return null;
        holdingPen.appendChild(rowEl);
        rowEl._displaced = false;
        return rowEl;
    }

    function _hideModalChrome() {
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
    }

    function cancelModal() {
        const rowEl = content.querySelector('.location-form');
        if (!rowEl) {
            _hideModalChrome();
            return;
        }
        if (rowEl._isFreshUnsaved) {
            // Discard a never-confirmed brand-new row. We mark
            // DELETE=on (rather than physically removing it from
            // the DOM + decrementing TOTAL_FORMS) because Django's
            // formset contract relies on every index 0..N-1 being
            // present in POST data. ``DeleteAwareFormMixin``
            // short-circuits the row's full_clean on the server
            // side, and ``rebuildTable`` already filters
            // ``new && deleted`` rows out of the table.
            _setRowDeleted(rowEl, true);
            holdingPen.appendChild(rowEl);
            rowEl._displaced = false;
        } else {
            // Existing row — revert any edits the user made in
            // this modal session, then return to the holding pen.
            _restoreValues(rowEl, rowEl._valueSnapshot);
            holdingPen.appendChild(rowEl);
            rowEl._displaced = false;
            refreshTableRowFor(rowEl);
        }
        _clearRequiredErrors(rowEl);
        rowEl._valueSnapshot = null;
        _hideModalChrome();
    }

    function confirmModal() {
        const rowEl = content.querySelector('.location-form');
        if (!rowEl) {
            _hideModalChrome();
            return;
        }
        if (!_validateModalRow(rowEl)) {
            // Keep the modal open so the office can fix the
            // freshly-painted "Ce champ est obligatoire."
            // messages without losing context.
            return;
        }
        const wasFresh = !!rowEl._isFreshUnsaved;
        rowEl._isFreshUnsaved = false;
        rowEl._valueSnapshot = null;
        holdingPen.appendChild(rowEl);
        rowEl._displaced = false;
        if (wasFresh) {
            // First successful Valider for this brand-new row — it
            // appears in the table NOW (not when the user clicked
            // "+ Ajouter une adresse").
            rebuildTable();
        } else {
            refreshTableRowFor(rowEl);
        }
        _hideModalChrome();
        // PO 2026-05-20 — confirm Valider with a success toast so the
        // operator has a clear "OK, c'est pris" signal after the modal
        // closes (errors stay inline inside the modal, cf.
        // ``_validateModalRow``). Follows the canonical pattern from
        // ``.cursor/rules/toast-feedback.mdc`` § 1.1 / § 4 line
        // « Edit/update via modal » → ``showToast`` after close,
        // route-scoped (auto-dismiss 4s, cleared on hx-boost
        // navigation). Mode-aware copy mirrors the table column
        // labels ("Adresse" in DIRECT, "Site d'intervention" in
        // CONTRACTOR). Defensive ``typeof`` guard so a stripped /
        // not-yet-loaded toast bundle silently no-ops instead of
        // throwing — the save itself doesn't depend on the toast.
        const mode = _currentRelationshipMode(formEl);
        const suffix = mode === 'contractor' ? 'Contractor' : 'Direct';
        const action = wasFresh ? 'Added' : 'Updated';
        const dsKey = 'location' + action + suffix; // locationAddedDirect, etc.
        const fallback = wasFresh ? 'Adresse & Contact ajoutée.' : 'Adresse & Contact mise à jour.';
        const msg = formEl.dataset[dsKey] || fallback;
        if (typeof window.showToast === 'function') {
            try { window.showToast(msg, 'success'); } catch { /* no-op */ }
        }
    }

    function refreshTableRowFor(rowEl) {
        const tr = tableBody.querySelector('tr[data-location-row="' + rowEl.dataset.locationRow + '"]');
        if (!tr) return;
        _renderRowSummary(rowEl, tr, _currentRelationshipMode(formEl));
    }

    // PR 2.1f-d — return every Location form that's visible in the
    // current table state (i.e. not a discarded fresh-unsaved row
    // and not marked DELETE=on). The result preserves form-index
    // order, which is the order the table renders them in.
    function _visibleRows() {
        return Array.from(
            holdingPen.querySelectorAll('.location-form')
        ).filter((rowEl) => !(_isRowNew(rowEl) && _isRowDeleted(rowEl))
                          && !_isRowDeleted(rowEl));
    }

    // PR 2.5 — ``_setBillingRow`` removed (per-row billing marker is gone).

    function rebuildTable() {
        const mode = _currentRelationshipMode(formEl);
        tableBody.innerHTML = '';
        const rows = holdingPen.querySelectorAll('.location-form');
        // PR 2.2-bis — « Afficher archivées » checkbox state. When
        // unchecked (default), archived rows are skipped entirely
        // so the table is the « actionable today » view ; when
        // checked, archived rows render dimmed via
        // ``_renderRowSummary`` and are recognisably non-active.
        const showArchivedToggle = formEl.querySelector('[data-show-archived-toggle]');
        const showArchived = !!(showArchivedToggle && showArchivedToggle.checked);
        // PR 2.2-bis — live count of « still active » rows
        // (persisted + active + not pending-delete). Drives the
        // « last active » block in ``_wireArchiveButtons`` : if
        // exactly one such row remains, its archive button is
        // disabled because archiving it would leave the customer
        // with zero pickable Locations. Recomputed every rebuild
        // because the count changes as the operator archives /
        // reactivates / deletes / adds rows in the same session.
        //
        // PR 2.2-bis follow-up (PO 2026-05-21) — ``usableRowCount``
        // is the live count of rows the customer can ACTUALLY use
        // = persisted-active + fresh-not-deleted. Drives the « last
        // usable » trash-icon gate, mirroring the « last active »
        // archive-icon gate. Differs from ``activeRowCount`` only
        // in that fresh rows count too (a brand-new row in the
        // formset becomes a live address on save, so deleting the
        // only fresh row would orphan the customer).
        let activeRowCount = 0;
        let archivedRowCount = 0;
        let usableRowCount = 0;
        rows.forEach((rowEl) => {
            const isPersisted = rowEl.dataset.isPersisted === 'true';
            if (_isRowDeleted(rowEl)) return;
            if (!isPersisted) {
                usableRowCount += 1;
                return;
            }
            if (_isRowActive(rowEl)) {
                activeRowCount += 1;
                usableRowCount += 1;
            } else {
                archivedRowCount += 1;
            }
        });
        // PR 2.2-bis follow-up (PO 2026-05-20) — the toggle wrap is
        // shown iff at least one archived row exists in the formset
        // RIGHT NOW (not just at GET-time). If the operator just
        // archived their first row of the session, the toggle MUST
        // appear so they can reveal it and click « Réactiver ».
        // Symmetrically, if they reactivate the last archived row,
        // the toggle disappears and the checkbox resets to unchecked
        // so a future re-archival starts the cycle fresh.
        const toggleWrap = formEl.querySelector('[data-archived-toggle-wrap]');
        if (toggleWrap) {
            if (archivedRowCount > 0) {
                toggleWrap.classList.remove('hidden');
            } else {
                toggleWrap.classList.add('hidden');
                if (showArchivedToggle && showArchivedToggle.checked) {
                    showArchivedToggle.checked = false;
                }
            }
        }
        let visibleCount = 0;
        rows.forEach((rowEl, idx) => {
            const isNew = _isRowNew(rowEl);
            const isDel = _isRowDeleted(rowEl);
            if (isNew && isDel) return; // discarded fresh row → no table line
            // PR 2.2-bis — archived row gating. Persisted rows whose
            // ``is_active`` was flipped to False are hidden by
            // default ; the toggle re-renders them dimmed when
            // checked. Fresh-unsaved rows are always considered
            // active (the JS default in ``_isRowActive``) so this
            // branch never affects them.
            if (!_isRowActive(rowEl) && !showArchived) return;
            if (!rowEl.dataset.locationRow) {
                rowEl.dataset.locationRow = 'row-' + idx + '-' + Math.random().toString(36).slice(2, 6);
            }
            const tr = rowTemplate.content.firstElementChild.cloneNode(true);
            tr.dataset.locationRow = rowEl.dataset.locationRow;
            const editBtn = tr.querySelector('[data-action="edit-row"]');
            const delBtn = tr.querySelector('[data-action="delete-row"]');
            const trashIcon = delBtn.querySelector('[data-action-icon="delete"]');
            const undoIcon = delBtn.querySelector('[data-action-icon="undo"]');
            // PR 2.2-bis follow-up #6 (PO 2026-05-21) — aria-label
            // localised via the row's ``{% trans %}``-rendered data-
            // attrs. Hardcoded text strings were leaking to screen-
            // readers in every non-default locale — same root cause
            // as the tooltip-text fix above. Fallbacks are English
            // (the msgid source language).
            const labelDelete = rowEl.dataset.tooltipDeleteDefault || 'Delete';
            const labelUndo = rowEl.dataset.tooltipDeleteUndo || 'Undo delete';
            // PR 2.2-bis follow-up (PO 2026-05-21) — when the row is
            // in « marked-for-delete » state the button serves as the
            // UNDO affordance, not a destructive one ; it must lose
            // the red tint (which signals « will destroy data » per
            // the project's action-icon color rule) and pick up the
            // primary-tint used by every other constructive recovery
            // affordance in the table (cf. the [data-action="reactivate
            // -row"] button in this same template). Lists kept as
            // local constants so a future refactor can move them
            // server-side via data-attrs without rewriting the swap.
            const DELETE_CLASSES = [
                'text-red-500', 'hover:text-red-700',
                'dark:text-red-400', 'dark:hover:text-red-300',
            ];
            const UNDO_CLASSES = [
                'text-primary-600', 'hover:text-primary-900',
                'dark:text-primary-400', 'dark:hover:text-primary-200',
            ];
            const setDeleteAffordance = (deleted) => {
                trashIcon.classList.toggle('hidden', deleted);
                undoIcon.classList.toggle('hidden', !deleted);
                delBtn.setAttribute('aria-label', deleted ? labelUndo : labelDelete);
                // PR 2.2-bis follow-up (PO 2026-05-21) — tooltip text +
                // button color must follow the affordance, not stay
                // pinned to the « Delete » initial state. Without this
                // hover on the row's trash-icon-turned-undo-icon kept
                // showing « Supprimer » and the button stayed red,
                // contradicting what the icon shape (undo arrow) and
                // the row's strikethrough were already telling the
                // operator. The tooltip update goes through
                // ``_setRowTooltipText`` which handles the rebind of
                // Flowbite's [data-tooltip-content] node + re-init
                // dance (same helper used by the archive / reactivate
                // / star tooltips).
                //
                // The ``!deleted`` branch only writes « Delete » when
                // the button is ENABLED ; for a disabled row (has-
                // jobs gate, archive gate) ``_wireArchiveButtons``
                // has already written the block-reason tooltip and
                // overwriting it would silently strip the explain-
                // why text the operator needs to understand why the
                // button is greyed.
                if (deleted) {
                    _setRowTooltipText(tr, 'data-delete-tooltip', labelUndo);
                } else if (!delBtn.disabled) {
                    _setRowTooltipText(tr, 'data-delete-tooltip', labelDelete);
                }
                if (deleted) {
                    delBtn.classList.remove(...DELETE_CLASSES);
                    delBtn.classList.add(...UNDO_CLASSES);
                } else {
                    delBtn.classList.remove(...UNDO_CLASSES);
                    delBtn.classList.add(...DELETE_CLASSES);
                }
            };
            editBtn.addEventListener('click', () => openModalFor(rowEl));

            // PO 2026-05-22 — same "open the edit modal" trigger on
            // the first column's text (label in DIRECT mode / end-
            // client name in CONTRACTOR mode). Mirrors the implicit
            // affordance of every other office list table where the
            // entity name is a link to the entity (customer name in
            // /office/customers/, invoice number in /office/invoices/,
            // …). The whole <td> reacts, not just the <span>, so the
            // hit target stays comfortable on touch screens. Visual
            // cues : ``cursor-pointer`` + ``hover:underline`` on the
            // text span so the row signals it's actionable. The star
            // / Edit / Delete buttons on the same row already
            // ``event.stopPropagation()`` via their dedicated click
            // handlers (each calls a specific helper without bubbling
            // further), so clicking an action icon inside the row
            // never accidentally also fires this cell-level handler.
            const primaryCell = tr.querySelector('td:first-child');
            if (primaryCell) {
                primaryCell.classList.add('cursor-pointer');
                const primarySpan = primaryCell.querySelector('[data-cell="primary"]');
                if (primarySpan) {
                    primarySpan.classList.add('hover:underline');
                }
                primaryCell.addEventListener('click', (ev) => {
                    // Defensive guard : if the operator clicks the
                    // « Facturation » star badge (rendered INSIDE the
                    // primary cell next to the label), that badge is
                    // purely informational and has no click handler
                    // of its own, so the click would bubble here and
                    // open the modal. That's actually the desired UX
                    // (badge click ⇒ open the modal to edit billing
                    // settings), so no guard is needed. The deleted-
                    // row check below covers the only real corner
                    // case : a soft-deleted row's primary cell is
                    // displayed greyed-out with a strikethrough ;
                    // clicking it should be inert.
                    if (_isRowDeleted(rowEl)) return;
                    // Don't double-fire when the inner Edit button
                    // is the actual click target (defensive : the
                    // button has its own listener, but if a future
                    // refactor moves it inside the primary cell the
                    // composite would otherwise open the modal twice).
                    if (ev.target.closest('[data-action]')) return;
                    openModalFor(rowEl);
                });
            }

            // PR 2.5 — star click handler removed (the star button
            // itself is gone; per-row billing marker no longer exists).

            // PR 2.2-bis follow-up #5 (PO 2026-05-20) — destructive
            // delete now goes through ``window.showConfirmModal``
            // BEFORE flipping ``DELETE=on``. Why not the declarative
            // ``data-confirm-action="submit-form"`` helper from
            // ``confirm-action.js`` : the action isn't a form-submit,
            // it's an in-page formset toggle (the DELETE input ships
            // with the rest of the customer form when the operator
            // clicks « Enregistrer les modifications »). So we read
            // the same ``data-confirm-*`` data-attrs the template
            // declares and route through the global modal helper
            // directly. The UNDO branch (clicking the trash icon
            // on an already-deleted row to restore it) deliberately
            // skips the modal — undoing a soft-delete is non-
            // destructive and an extra modal would feel like
            // friction without payoff. Same goes for the disabled-
            // button defensive guard above.
            const performDelete = () => {
                // PR 2.5 — the billing-heir transfer (previously
                // needed to keep the partial UniqueConstraint
                // happy on the customer's at-most-one billing
                // Location) is gone with the marker itself.
                _setRowDeleted(rowEl, true);
                if (_isRowNew(rowEl)) {
                    tr.remove();
                    rowEl.classList.add('hidden');
                    updateEmptyState();
                } else {
                    refreshTableRowFor(rowEl);
                    setDeleteAffordance(true);
                }
            };

            delBtn.addEventListener('click', () => {
                // PR 2.2-bis — defensive gate. The button is
                // visually disabled via ``_wireArchiveButtons``
                // when the row has Jobs attached, but a stale tab
                // or a keyboard space-press on a focused-but-
                // disabled button could still fire the click ;
                // re-check here and silently no-op rather than
                // sending a hard-delete that the server would
                // reject with a 422 anyway.
                if (delBtn.disabled) return;
                if (_isRowDeleted(rowEl)) {
                    // Undo branch — no confirmation needed (the
                    // operator is RESTORING data, not destroying
                    // it). Stays direct on purpose.
                    _setRowDeleted(rowEl, false);
                    refreshTableRowFor(rowEl);
                    setDeleteAffordance(false);
                    return;
                }
                // Destructive branch — surface the global confirm
                // modal first, then perform the delete. We read
                // every modal string from the trigger's data-
                // attributes so the i18n stays in the template
                // (no hardcoded French in the JS). If the global
                // helper is missing (very early load / bundling
                // hiccup) we fall back to a native ``confirm()``
                // so the destructive action still gates on user
                // acknowledgement rather than firing silently.
                //
                // PR 2.2-bis follow-up (PO 2026-05-21) — pick the
                // right confirm message based on whether the row is
                // persisted or fresh-unsaved. The fixed message was
                // misleading on a brand-new row : it referenced an
                // « icône de restauration » that never appears for
                // unpersisted rows (``performDelete`` does ``tr.
                // remove()`` for new rows, so there is no row left
                // to host the undo affordance). Falls back to the
                // generic message if the template hasn't been
                // updated yet (forward-compat).
                const ds = delBtn.dataset;
                const isNew = _isRowNew(rowEl);
                const message = isNew
                    ? (ds.confirmMessageNew || ds.confirmMessage || '')
                    : (ds.confirmMessage || '');
                const config = {
                    title: ds.confirmTitle || 'Confirm',
                    message,
                    confirmText: ds.confirmText || 'Confirm',
                    cancelText: ds.confirmCancelText || 'Cancel',
                    type: 'danger',
                    onConfirm: performDelete,
                };
                if (typeof window.showConfirmModal === 'function') {
                    window.showConfirmModal(config);
                } else if (window.confirm(config.message || config.title)) {
                    performDelete();
                }
            });
            tableBody.appendChild(tr);
            _renderRowSummary(rowEl, tr, mode);
            _wireArchiveButtons(rowEl, tr, rebuildTable, activeRowCount, usableRowCount);
            setDeleteAffordance(_isRowDeleted(rowEl));
            visibleCount += 1;
        });
        if (tableEmpty) tableEmpty.classList.toggle('hidden', visibleCount > 0);
    }

    function updateEmptyState() {
        const visibleRows = tableBody.querySelectorAll('tr.location-table-row').length;
        if (tableEmpty) tableEmpty.classList.toggle('hidden', visibleRows > 0);
    }

    addBtn.addEventListener('click', function () {
        const count = parseInt(totalForms.value, 10);
        const html = template.innerHTML.replace(/__prefix__/g, count);
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const newForm = wrapper.firstElementChild;
        holdingPen.appendChild(newForm);
        totalForms.value = count + 1;

        // Initialise phone inputs on the cloned row so libphonenumber
        // formatting works inside the modal.
        const phoneInput = newForm.querySelector('input[data-phone-input]');
        if (phoneInput && typeof initPhoneInput === 'function') {
            try { initPhoneInput(phoneInput); } catch { /* */ }
        }

        // PR 2.5 — the auto-mark-first-row-as-billing logic is
        // gone with the per-row marker itself. The customer's
        // billing address now lives on Customer.billing_address_*
        // on its own card.

        // PR 2.1f-c follow-up (PO 2026-05-20) — mark the row as
        // fresh-unsaved so cancel discards it (DELETE=on) and the
        // table only gets a real entry once "Valider" succeeds.
        // The previous code called ``rebuildTable()`` here, which
        // painted an empty "— Nouvelle adresse —" line that
        // survived Cancel/Escape and confused the office.
        newForm._isFreshUnsaved = true;
        openModalFor(newForm);
    });

    // Modal close buttons + Escape are handled by the
    // dynamic-modal helper for the EndClient modal; this Location
    // modal uses lighter-weight wiring because no AJAX is involved.
    modal.querySelectorAll('[data-location-modal-close]').forEach(btn => {
        btn.addEventListener('click', cancelModal);
    });
    const confirmBtn = modal.querySelector('[data-location-modal-confirm]');
    if (confirmBtn) confirmBtn.addEventListener('click', confirmModal);
    document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && !modal.classList.contains('hidden')) cancelModal();
    });

    // PR 2.2-bis — wire the « Afficher archivées » toggle. The
    // template only renders the checkbox when at least one
    // persisted row carries ``is_active=False`` (cf.
    // ``BaseLocationFormSet._has_archived_rows``), so the
    // ``querySelector`` returns ``null`` for the « no archived
    // rows yet » case (no work to do — every rebuild renders
    // every row, which is what we want).
    const showArchivedToggle = formEl.querySelector('[data-show-archived-toggle]');
    if (showArchivedToggle) {
        showArchivedToggle.addEventListener('change', () => rebuildTable());
    }

    // Auto-open the modal on initial paint if any row carries a
    // server-side ``.field-error`` (= validation 422 came back). We
    // pick the first erroring row so the operator immediately sees
    // what needs fixing instead of hunting for the "⚠" badge.
    rebuildTable();
    const erroringRow = holdingPen.querySelector('.location-form .field-error');
    if (erroringRow) {
        const row = erroringRow.closest('.location-form');
        if (row) openModalFor(row);
    }

    // Re-run on relationship-type changes so the first column
    // label (« Libellé » ⇄ « Client final ») + the per-row
    // « Réf. : … » CONTRACTOR-only hint flip accordingly.
    formEl.addEventListener('change', (ev) => {
        const t = ev.target;
        if (t && t.name && /relationship_type$/.test(t.name)) {
            rebuildTable();
        }
        // When a select inside a displaced row changes (typically the
        // EndClient picker), keep the table row in sync.
        if (t && t.name && /^locations-\d+-/.test(t.name)) {
            const row = t.closest('.location-form');
            if (row) refreshTableRowFor(row);
        }
    });

    // Expose for tests / debugging. ``_closeLocationModal`` is the
    // public counterpart to ``openModalFor`` ; it points at
    // ``cancelModal`` (the « X » / Escape / Annuler path) so a test
    // can close the modal in its discard-changes mode. Use
    // ``confirmModal`` directly via DOM (``[data-location-modal-
    // confirm]``) when the test wants to exercise the save path.
    //
    // PO 2026-05-20 (PR 2.1f-h) — was ``= closeModal`` (undefined
    // identifier, leftover from a 2.1f-b rename refactor) which
    // raised a ``ReferenceError`` synchronously at the end of
    // ``initLocationFormset()``, breaking the test/debug
    // ``_openLocationModalFor`` / ``_rebuildLocationsTable``
    // exposures further up — they did get set, but ``app.js::
    // _runAutoInit`` then saw an uncaught exception and aborted
    // the rest of the auto-init pass for subsequent
    // ``[data-auto-init]`` nodes on the same swap.
    formEl._rebuildLocationsTable = rebuildTable;
    formEl._openLocationModalFor = openModalFor;
    formEl._closeLocationModal = cancelModal;
}

// PR 2.1f-a — ``initContactFormset()`` removed alongside the dropped
// ``Contact`` model. Site contact data (name / phone / email) is now
// part of each Location row in the locations formset; the per-row
// ``data-phone-input`` decoration is already handled by
// ``initAllPhoneInputs(formEl)`` below and by ``initLocationFormset``
// for cloned rows.

// ========================================================
// MAIN INIT
// ========================================================
// NB: submit-button protection is handled globally by
// `frontend/src/utils/forms/submit-button.js` via `data-protect-submit`
// on the form root. Do not reintroduce a per-form spinner here.

/**
 * @param {Object} config
 * @param {string}  [config.formId]             - Form element ID (default: 'customer-form')
 *
 * NOTE: ``billingHints`` (per_visit / monthly_grouped) was removed in
 * PR-4b (2026-05-25). The customer-level billing mode no longer
 * exists on the form; the equivalent on the WorkSite form is wired
 * by ``worksite-form.js``.
 */
export function initCustomerForm(config = {}) {
    const { formId = 'customer-form' } = config;

    const formEl = document.getElementById(formId);
    if (!formEl) return;

    initAllPhoneInputs(formEl);
    initValidation(formEl);

    const vatRefs = initCustomerType();
    initVatLiveFeedback(formEl, vatRefs);
    initCompanyLookup(formEl, vatRefs);
    wireRelationshipTypeToggleOnce(formEl);
    initLocationFormset();

    // PR 2.6 — Live IBAN → BIC auto-derive (server-side schwifty lookup
    // via /office/api/derive-bic/). The customer form uses Django's
    // ``prefix='customer'`` so the real input names are e.g.
    // ``customer-iban`` / ``customer-bic`` — match both the plain and
    // prefixed shapes so the helper works regardless of how the form
    // is instantiated. No-op when either input is absent.
    attachBicAutoDerive({
        ibanInput: formEl.querySelector('input[name="iban"], input[name$="-iban"]'),
        bicInput: formEl.querySelector('input[name="bic"], input[name$="-bic"]'),
    });

    initUnsavedChangesGuard(formEl);
}

// ─────────────────────────────────────────────────────────────────────
// PO 2026-05-21 — Phase 2 of the customer-detail unification :
// « Unsaved changes » guard on tab switch + Cancel button + raw
// browser navigation.
//
// Design (cf. docs/CUSTOMER_DETAIL_UNIFICATION.md § 3 phase 2) :
//
// 1. Dirty tracking — first ``input`` / ``change`` event on the form
//    flips a module-level ``_isDirty`` flag. Simple « first-edit-
//    marks-dirty » heuristic ; the user can still navigate freely
//    until they touch a field. Standard CRM behaviour.
//
// 2. Validation-errors heuristic — if the server re-rendered the form
//    with field errors (POST → render same-page flow), the operator's
//    in-input edits are still there but unsaved. Treat that as dirty
//    too so the tab-switch guard catches them.
//
// 3. Tab-switch interception — capture-phase click listener on every
//    tab link (``a[href*="?tab="]`` inside ``[role="tablist"]``).
//    Runs BEFORE HTMX boost handlers ; preventDefault + stopImmediate
//    when dirty, open the modal with the intended target URL.
//
// 4. Cancel button — same guard. Cancel is semantically « navigate
//    back to ``?tab=identity`` reset state », so a dirty form means
//    the operator might actually want to keep their edits.
//
// 5. Modal (3 buttons) :
//    * Cancel   — close modal, stay put.
//    * Discard  — reset dirty flag + navigate (loses edits).
//    * Save     — AJAX POST the form, on success navigate to target,
//                 on validation errors swap the form HTML partial
//                 with the bound error version (keeps the operator's
//                 inputs + shows the field errors), close modal.
//
// 6. ``beforeunload`` safety net — for raw browser navigation that
//    can't be intercepted client-side (close tab, refresh, type URL).
//    Triggers the browser's native « Leave site? » dialog (text not
//    customisable).
//
// State management : module-level ``let`` so we survive AJAX-driven
// form-partial swaps (cf. hx-boost-init.mdc § 1 — module-level flags
// for ``document.body`` / ``window`` listeners). The ``_activeFormEl``
// reference is updated on every init so the modal's Save button
// always reads the CURRENT form, not the (now-detached) old one.
// ─────────────────────────────────────────────────────────────────────

let _isDirty = false;
let _activeFormEl = null;
let _pendingNavigation = null;
let _beforeUnloadBound = false;

// Re-init clears _isDirty unless the form was server-rendered with
// errors (POST → bound forms → operator's edits still in inputs).
// Detection : ``.field-error`` markers from ``_form_content.html``.
function detectServerSideErrors(formEl) {
    return formEl.querySelectorAll('.field-error, [class*="text-red-600"]').length > 0;
}

function bindBeforeUnloadOnce() {
    if (_beforeUnloadBound) return;
    _beforeUnloadBound = true;
    window.addEventListener('beforeunload', (e) => {
        if (_isDirty) {
            e.preventDefault();
            // Required for Chrome to actually show the native dialog.
            e.returnValue = '';
        }
    });
}

function initUnsavedChangesGuard(formEl) {
    // Per-element flag (rule 3 of hx-boost-init.mdc) — re-binds
    // after AJAX form-partial swap because the new form is a fresh
    // DOM node without the flag.
    if (!formEl || formEl._unsavedGuardBound) {
        // Keep the module-level reference up to date even on re-init
        // skip — the modal's Save handler always reads _activeFormEl.
        if (formEl) _activeFormEl = formEl;
        return;
    }
    formEl._unsavedGuardBound = true;

    _activeFormEl = formEl;
    _isDirty = detectServerSideErrors(formEl);

    const markDirty = () => { _isDirty = true; };
    formEl.addEventListener('input', markDirty);
    formEl.addEventListener('change', markDirty);

    // Regular « Save Changes » submit = explicit user save intent :
    // clear dirty so the beforeunload prompt doesn't fire on the
    // POST-Redirect-GET roundtrip.
    formEl.addEventListener('submit', () => { _isDirty = false; });

    bindBeforeUnloadOnce();
    bindTabAndCancelInterceptors();
    bindModalHandlersOnce();
}

function bindTabAndCancelInterceptors() {
    // Tab links — query every time (re-init after page navigation
    // sees fresh nodes since #main-content gets swapped by boost).
    // Capture phase so we run BEFORE HTMX boost (which listens in
    // bubble phase on document.body).
    const tabLinks = document.querySelectorAll('[role="tablist"] a[href*="?tab="]');
    tabLinks.forEach((link) => {
        if (link._unsavedGuardBound) return;
        link._unsavedGuardBound = true;
        link.addEventListener('click', (e) => {
            if (!_isDirty) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            openUnsavedModal(link.href);
        }, true);
    });

    // « Annuler » button on the form — semantically a navigate-to
    // ``?tab=identity`` reset. Guard it the same way as the tabs ;
    // if not dirty just navigate directly (matches Phase 1 behaviour).
    const cancelBtn = document.querySelector('[data-customer-form-cancel]');
    if (cancelBtn && !cancelBtn._unsavedGuardBound) {
        cancelBtn._unsavedGuardBound = true;
        cancelBtn.addEventListener('click', () => {
            const targetUrl = window.location.pathname + '?tab=identity';
            if (_isDirty) {
                openUnsavedModal(targetUrl);
            } else {
                window.location.href = targetUrl;
            }
        });
    }
}

function bindModalHandlersOnce() {
    const modal = document.getElementById('unsaved-changes-modal');
    if (!modal || modal._unsavedModalBound) return;
    modal._unsavedModalBound = true;

    // PO 2026-05-22 — multiple [data-unsaved-cancel] targets are
    // legit: the "Cancel" button in the footer AND the X close
    // button in the header (added per PO feedback). Both share
    // the exact same semantics — abort the navigation, keep the
    // form dirty, leave the modal — so they share the same
    // listener via a single querySelectorAll loop.
    const cancelBtns = modal.querySelectorAll('[data-unsaved-cancel]');
    const discardBtn = modal.querySelector('[data-unsaved-discard]');
    const saveBtn = modal.querySelector('[data-unsaved-save]');

    cancelBtns.forEach((btn) => {
        btn.addEventListener('click', closeUnsavedModal);
    });
    if (discardBtn) {
        discardBtn.addEventListener('click', () => {
            const target = _pendingNavigation;
            _isDirty = false;
            _pendingNavigation = null;
            closeUnsavedModal();
            if (target) window.location.href = target;
        });
    }
    if (saveBtn) {
        saveBtn.addEventListener('click', () => saveAndContinue(saveBtn));
    }
}

async function saveAndContinue(saveBtn) {
    if (!_activeFormEl) return;
    saveBtn.disabled = true;
    try {
        const formData = new FormData(_activeFormEl);
        const resp = await fetch(_activeFormEl.action, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json, text/html',
            },
            credentials: 'same-origin',
        });

        const ct = resp.headers.get('content-type') || '';
        if (resp.ok && ct.includes('application/json')) {
            // Success — navigate to the operator's intended target.
            const target = _pendingNavigation;
            _isDirty = false;
            _pendingNavigation = null;
            closeUnsavedModal();
            if (target) {
                window.location.href = target;
            } else {
                window.location.reload();
            }
            return;
        }

        if (resp.ok && ct.includes('text/html')) {
            // Validation errors — server returned the form partial
            // with bound forms + field errors. Swap the form DOM
            // (preserves the modal) and let the operator fix it.
            const html = await resp.text();
            closeUnsavedModal();
            _pendingNavigation = null;
            swapFormPartial(_activeFormEl, html);
            return;
        }

        // Unexpected status / content-type — re-enable the button so
        // the operator can retry ; leave the modal open. A future
        // iteration could surface a toast here.
        saveBtn.disabled = false;
    } catch (_err) {
        // Network failure — same as above, give the operator another
        // shot at the Save button.
        saveBtn.disabled = false;
    }
}

function swapFormPartial(currentFormEl, newHtml) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = newHtml.trim();
    const newFormEl = wrapper.querySelector('#customer-form');
    if (!newFormEl) {
        // Defensive fallback — server response didn't include the
        // expected form. Force a fresh GET so the operator at least
        // sees a coherent page.
        window.location.reload();
        return;
    }
    currentFormEl.replaceWith(newFormEl);
    // Re-run auto-init on the new form node so phone inputs / custom
    // selects / validation / archive buttons / and the guard itself
    // are bound to the new DOM. The modal stays in place (it lives
    // outside the form per the partial layout) — only its
    // ``_activeFormEl`` reference gets updated by the re-init.
    autoInitCustomerForm();
}

function openUnsavedModal(targetUrl) {
    _pendingNavigation = targetUrl;
    const modal = document.getElementById('unsaved-changes-modal');
    if (modal) modal.classList.remove('hidden');
}

function closeUnsavedModal() {
    const modal = document.getElementById('unsaved-changes-modal');
    if (modal) modal.classList.add('hidden');
}

/**
 * Auto-initialize from data attributes on #customer-form.
 * Called by the Modal's executeScripts via a minimal inline trigger,
 * or directly when the form is present on initial page load.
 */
export function autoInitCustomerForm() {
    var formEl = document.getElementById('customer-form');
    if (!formEl) return;

    initCustomerForm({
        formId: 'customer-form',
    });
}
